import {
  AccountDocumentBasicResponse,
  AccountDocumentDetailResponse,
  AccountDocumentForm,
  AccountDocumentSignerStatsResponse,
  AccountHierarchyFlat,
  AccountHierarchyNested,
  AccountHierarchyResponseTypeEnum,
  CreateInvoicePaymentRequest,
  IAccountHistoryResp,
  IAccountOverviewStat,
  IAccountRespSchema,
  IBillGroupResp,
  IBillGroupStats,
  ICreditNoteReqSchema,
  ICreditNoteSchema,
  IInvoiceContactUpdateSchema,
  IInvoiceRespSchema,
  IInvoiceUpdateSchema,
  IManualPayment,
  ImportablePaymentMethodResp,
  ImportPaymentMethodResp,
  InvoiceSummaryResp,
  IOverviewItem,
  IOverviewResponse,
  IPayment,
  IPaymentGatewayAccountSchema,
  IPaymentInstructionSchema,
  IPaymentMethodReq,
  IPaymentMethodResp,
  IRefundSchema,
  IStorage,
  IUsageEventRequestSchema,
  IUsageEventResSchema,
  IUsageEventUpdateReqSchema,
  IVoidCreditSchema,
  PaymentGatewayAccountUnlinkReq,
  PaymentGatewayClientSecretResponse,
  PaymentMethodShareRequest,
  RecordShareAccessResponse,
  SendBillingEmailResponse,
  SetupIntentResponse,
  WireTransferPaymentMethodCreateReq,
} from '@monetize/types/app';
import {
  ApiListResponse,
  DEFAULT_PAGER,
  GenericApiResponse,
  GetListApiConfig,
  GetListApiFilter,
  Maybe,
} from '@monetize/types/common';
import {
  useMutation,
  UseMutationOptions,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { AxiosError, AxiosHeaders } from 'axios';
import groupBy from 'lodash/groupBy';
import { sortByAlphabetically } from '../utils';
import { arrayToObject, orderObjectsBy } from '../utils/misc';
import { getGroupedAndSortedPaymentMethodOptions } from '../utils/payment.utils';
import api, { apiDelete, apiGet, apiPost, apiPut, apiUpload } from './axios';
import { invoiceServiceQueryKeys } from './invoiceService';
import {
  accountServiceQueryKeys,
  subscriptionServiceQueryKeys,
} from './queryKeysService';
import { updateListCacheWithUpdatedItem } from './queryUtilsHelpers';
import { enqueueForQuote } from './quoteWriteChain';
import { composeGetQuery } from './utils';

// accounts
/** TODO: @deprecate */
export const getAccountById = async (accountId: string) => {
  const res = await apiGet<IAccountRespSchema>(`/api/accounts/${accountId}`);
  return res.data;
};

export const doGetBillGroupById = async (billGroupId: string) => {
  const res = await apiGet<IBillGroupResp>(`/api/billGroups/${billGroupId}`);
  return res.data;
};

export function useGetBillGroupStatsById(
  billGroupId: string,
  options: Partial<
    UseQueryOptions<IBillGroupStats, unknown, IBillGroupStats>
  > = {},
) {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.billGroups.billGroupStatsById(billGroupId),
    ],
    queryFn: () =>
      apiGet<IBillGroupStats>(`/api/billGroups/${billGroupId}/stats`).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useGetAccountById(
  accountId: string,
  options: Partial<
    UseQueryOptions<IAccountRespSchema, unknown, IAccountRespSchema>
  > = {},
) {
  return useQuery({
    queryKey: [...accountServiceQueryKeys.accounts.accountDetail(accountId)],
    queryFn: () =>
      apiGet<IAccountRespSchema>(`/api/accounts/${accountId}`).then(
        (res) => res.data,
      ),
    ...options,
  });
}

/** TODO: @deprecate */
export const getAccountList = async (
  config: GetListApiConfig,
  filters?: GetListApiFilter,
): Promise<ApiListResponse<IAccountRespSchema>> => {
  const params = composeGetQuery(config, filters);
  const res = await apiGet<ApiListResponse<IAccountRespSchema>>(
    '/api/accounts',
    {
      params,
    },
  );
  return res.data;
};

export function useGetAccountList({
  config,
  filters,
  options,
  useSearchApi = false,
}: {
  config: GetListApiConfig;
  filters?: GetListApiFilter;
  options?: Partial<
    UseQueryOptions<ApiListResponse<IAccountRespSchema>, unknown>
  >;
  /** If true, will call the search API, which expects query=searchTerm parameter */
  useSearchApi?: boolean;
}) {
  const params = composeGetQuery(config, filters);
  const endpoint = useSearchApi ? '/api/accounts/search' : '/api/accounts';
  return useQuery({
    queryKey: [...accountServiceQueryKeys.accounts.accountList(), params],
    queryFn: () =>
      apiGet<ApiListResponse<IAccountRespSchema>>(endpoint, {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

export function useCreateAccount(
  options: Partial<
    UseMutationOptions<IAccountRespSchema, unknown, Partial<IAccountRespSchema>>
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) =>
      apiPost<IAccountRespSchema>('/api/accounts', payload).then(
        (res) => res.data,
      ),
    onSuccess: (...rest) => {
      queryClient.invalidateQueries({
        queryKey: accountServiceQueryKeys.accounts.accountList(),
      });
      queryClient.setQueryData(
        [...accountServiceQueryKeys.accounts.base, rest[0]?.id],
        rest[0],
      );
      onSuccess && onSuccess(...rest);
    },
    ...restOptions,
  });
}
export function useUpdateAccount(
  options: Partial<
    UseMutationOptions<
      IAccountRespSchema,
      unknown,
      { id: string; data: Partial<IAccountRespSchema> }
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }) =>
      apiPut<IAccountRespSchema>(`/api/accounts/${id}`, data).then(
        (res) => res.data,
      ),
    onSuccess: (...rest) => {
      const [account] = rest;
      if (account?.id) {
        queryClient.setQueryData(
          [...accountServiceQueryKeys.accounts.accountDetail(account.id)],
          rest[0],
        );
      }
      onSuccess && onSuccess(...rest);
    },
    ...restOptions,
  });
}

export function useUpdateAccountAddress(
  options: Partial<
    UseMutationOptions<
      IAccountRespSchema,
      unknown,
      {
        accountId: string;
        type: 'billing' | 'shipping';
        data: {
          line1?: Maybe<string>;
          line2?: Maybe<string>;
          city?: Maybe<string>;
          state?: Maybe<string>;
          postalCode?: Maybe<string>;
          country?: Maybe<string>;
          description?: Maybe<string>;
        };
      }
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ accountId, type, data }) =>
      apiPut<IAccountRespSchema>(
        `/api/accounts/${accountId}/addresses?type=${type}`,
        data,
      ).then((res) => res.data),
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accounts.accountDetail(
            variables.accountId,
          ),
        ],
      });
      onSuccess && onSuccess(response, variables, context);
    },
    ...restOptions,
  });
}

export const doDeactivateAccount = async (
  id: string,
): Promise<GenericApiResponse> => {
  const res = await apiPut<GenericApiResponse>(`/api/accounts/${id}/cancel`);
  return res.data;
};

export const doActivateAccount = async (
  id: string,
): Promise<IAccountRespSchema> => {
  const res = await apiPut<IAccountRespSchema>(`/api/accounts/${id}/activate`);
  return res.data;
};

// Sample ends

// TODO-BREN remove the function below once it's unused
export const fetchAccountsService = async (): Promise<IAccountRespSchema[]> =>
  api.get('/api/accounts').then((res) => res.data.content);

export const createAccountService = async (payload: any): Promise<any> =>
  api.post('/api/accounts/', payload).then((res) => res.data);

export const updateAccountService = async (
  payload: any,
  accountId: string,
): Promise<any> =>
  api
    .put(`/api/accounts/${accountId}`, { ...payload, status: payload.status })
    .then((res) => res.data);

export const fetchAccountDetailsByAccountId = async (
  accountId: string,
): Promise<IAccountRespSchema[]> =>
  api.get(`/api/accounts/${accountId}`).then((res) => res.data);

export const doGetAccountHistory = async (
  accountId: string,
  config: GetListApiConfig,
  filters?: GetListApiFilter,
): Promise<ApiListResponse<IAccountHistoryResp>> => {
  const url = `/api/account/${accountId}/history`;
  const params = composeGetQuery(config, filters);
  const res = await apiGet<ApiListResponse<IAccountHistoryResp>>(url, {
    params,
  });
  return res.data;
};

export function useGetAccountOverview(
  accountId: string,
  billGroupId?: string,
  options: Partial<
    UseQueryOptions<IOverviewResponse, unknown, IOverviewItem[]>
  > = {},
) {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accounts.accountDetailOverview(accountId),
    ],
    queryFn: () =>
      apiGet<IOverviewResponse>(
        `/api/v2/accounts/${accountId}/subscriptions/overview`,
      ).then(({ data }) => data),

    select: ({ subscriptionOverviewsByBillGroup }) => {
      const filteredData = billGroupId
        ? subscriptionOverviewsByBillGroup.filter(
            ({ billgroup }) => billgroup.id === billGroupId,
          )
        : subscriptionOverviewsByBillGroup;
      const orderedData = orderObjectsBy(filteredData, [
        'billgroup.name',
        'billgroup.id',
      ] as any);

      return orderedData.map(({ subscriptionOverviews, ...rest }) => {
        return {
          ...rest,
          subscriptionOverviews: subscriptionOverviews.sort(
            sortByAlphabetically('name'),
          ),
        };
      });
    },
    ...options,
  });
}
export const useGetAccountOverviewStats = (
  accountId: string,
  options: Partial<UseQueryOptions<IAccountOverviewStat>> = {},
) => {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accounts.accountDetailOverview(accountId),
      'stat',
    ],
    queryFn: () =>
      apiGet<IAccountOverviewStat>(`/api/accounts/${accountId}/overview`).then(
        ({ data }) => data,
      ),
    ...options,
  });
};

export function useCreateUsageRecord(
  options: Partial<
    UseMutationOptions<
      IUsageEventResSchema,
      unknown,
      IUsageEventRequestSchema[]
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();
  return useMutation<IUsageEventResSchema, unknown, IUsageEventRequestSchema[]>(
    {
      mutationFn: (payload) =>
        apiPost<IUsageEventResSchema>(`/usage/events`, payload).then(
          (res) => res.data,
        ),

      onSuccess: (data, payload, context) => {
        queryClient.invalidateQueries({
          queryKey: [...accountServiceQueryKeys.usage.usageListBase()],
        });
        payload.forEach((usageRecord) => {
          queryClient.invalidateQueries({
            queryKey: [
              ...subscriptionServiceQueryKeys.subscriptionUsageConsumption({
                subscriptionId: usageRecord.subscriptionId,
              }),
            ],
          });
        });
        onSuccess && onSuccess(data, payload, context);
      },

      ...restOptions,
    },
  );
}

export function useEditUsageRecord(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { id: string; payload: IUsageEventUpdateReqSchema }
    >
  > = {},
) {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();
  return useMutation<
    void,
    unknown,
    { id: string; payload: IUsageEventUpdateReqSchema }
  >({
    mutationFn: ({ id, payload }) =>
      apiPut<void>(`/usage/events/${id}`, payload).then((res) => res.data),

    onSuccess: (data, variables, context) => {
      const { id, payload } = variables;
      updateListCacheWithUpdatedItem(
        queryClient,
        [...accountServiceQueryKeys.usage.usageListBase()],
        { id, ...payload },
        true,
      );
      queryClient.invalidateQueries({
        queryKey: [...accountServiceQueryKeys.usage.usageListBase()],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

// recent payments apis
export function useGetPaymentsByAccount<SelectData = ApiListResponse<IPayment>>(
  {
    accountId,
    config,
    filters,
  }: {
    accountId: string;
    config?: GetListApiConfig;
    filters?: GetListApiFilter;
  },
  options: Partial<
    UseQueryOptions<ApiListResponse<IPayment>, unknown, SelectData>
  > = {},
) {
  let params = {};
  if (config) {
    params = composeGetQuery(config, filters);
  }
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.payments.paymentByAccount(accountId),
      params,
    ],
    queryFn: async () =>
      apiGet<ApiListResponse<IPayment>>(`/api/accounts/${accountId}/payments`, {
        params,
      }).then((res) => res.data),
    ...options,
  });
}

export function useGetPaymentById(
  accountId: string,
  paymentId: string,
  options: Partial<UseQueryOptions<IPayment>> = {},
) {
  return useQuery({
    queryKey: [...accountServiceQueryKeys.payments.paymentById(paymentId)],
    queryFn: () =>
      apiGet<IPayment>(`/api/accounts/${accountId}/payments/${paymentId}`).then(
        (res) => res.data,
      ),
    enabled: !!accountId && !!paymentId,
    meta: { showErrorToast: true },
    ...options,
  });
}

export function useInvoiceListByAccount<
  SelectData = ApiListResponse<InvoiceSummaryResp>,
>(
  {
    accountId,
    config,
    filters,
  }: {
    accountId: string;
    config?: GetListApiConfig;
    filters?: GetListApiFilter;
  },
  options: Partial<
    UseQueryOptions<ApiListResponse<InvoiceSummaryResp>, unknown, SelectData>
  > = {},
) {
  let params = {};
  if (config) {
    params = composeGetQuery(config, filters);
  }
  return useQuery({
    queryKey: [accountServiceQueryKeys.accounts.invoiceList(accountId), params],
    queryFn: async () =>
      apiGet<ApiListResponse<InvoiceSummaryResp>>(
        `/api/accounts/${accountId}/invoices`,
        {
          params,
        },
      ).then((res) => res.data),
    ...options,
  });
}

// payment methods apis

export const doCreatePaymentMethodByAccount = async (
  accountId: string,
  data: IPaymentMethodReq,
) => {
  const res = await apiPost<IPaymentMethodResp>(
    `/api/accounts/${accountId}/paymentMethods`,
    data,
  );
  return res.data;
};

/** @deprecated, use useGetPaymentMethodsByAccount instead */
export const doGetPaymentMethodsByAccount = async (
  signal: AbortSignal,
  accountId: string,
  config?: GetListApiConfig,
  filters?: GetListApiFilter,
): Promise<ApiListResponse<IPaymentMethodResp>> => {
  let params = {};
  if (config) {
    params = composeGetQuery(config, filters);
  }
  const res = await apiGet<ApiListResponse<IPaymentMethodResp>>(
    `/api/accounts/${accountId}/paymentMethods`,
    {
      params,
      signal,
    },
  );
  return res.data;
};

export function useGetPaymentMethodsByAccount<
  SelectData = ApiListResponse<IPaymentMethodResp>,
>(
  {
    accountId,
    config,
    filters,
  }: {
    accountId: string;
    config?: GetListApiConfig;
    filters?: GetListApiFilter;
  },
  options: Partial<
    UseQueryOptions<ApiListResponse<IPaymentMethodResp>, unknown, SelectData>
  > = {},
) {
  const queryClient = useQueryClient();
  let params = {};
  if (config) {
    params = composeGetQuery(config, filters);
  }
  const response = useQuery({
    queryKey: [
      ...accountServiceQueryKeys.paymentMethods.paymentMethodList(accountId),
      params,
    ],
    queryFn: ({ signal }) =>
      apiGet(`/api/accounts/${accountId}/paymentMethods`, {
        params,
        signal,
      }).then((res) => res.data),
    meta: {
      setByIdCacheFromReturnedList: {
        byIdQueryKey: (id) =>
          accountServiceQueryKeys.paymentMethods.paymentMethod(id),
      },
    },
    ...options,
  });

  return response;
}

/**
 * Get payment methods by account where items are grouped for easy lookup
 *
 */
export const useGetPaymentMethodsByAccountWithGroupedItems = ({
  accountId,
  filters,
  filterFn,
}: {
  accountId: string;
  filters: GetListApiFilter;
  filterFn?: (data: IPaymentMethodResp) => boolean;
}) => {
  return useGetPaymentMethodsByAccount<
    ApiListResponse<IPaymentMethodResp> & {
      paymentMethodsByGatewayId: Record<
        string,
        ReturnType<
          typeof getGroupedAndSortedPaymentMethodOptions<IPaymentMethodResp>
        >
      >;
      paymentMethodsById: Record<string, IPaymentMethodResp>;
    }
  >(
    {
      accountId,
      config: DEFAULT_PAGER,
      filters,
    },
    {
      select: (data) => {
        // Since we do not support a NOT IN query, filter out manually
        const _paymentMethods = data.content.filter((item) =>
          filterFn ? filterFn(item) : true,
        );

        const paymentMethodsByGatewayId = groupBy(
          _paymentMethods,
          'paymentGateway.id',
        );

        return {
          ...data,
          paymentMethodsByGatewayId: Object.keys(
            paymentMethodsByGatewayId,
          ).reduce(
            (
              acc: Record<
                string,
                ReturnType<
                  typeof getGroupedAndSortedPaymentMethodOptions<IPaymentMethodResp>
                >
              >,
              gatewayId,
            ) => {
              acc[gatewayId] = getGroupedAndSortedPaymentMethodOptions(
                paymentMethodsByGatewayId[gatewayId],
              );
              return acc;
            },
            {},
          ),
          paymentMethodsById: arrayToObject(_paymentMethods, 'id'),
        };
      },
    },
  );
};

export function useCreateInvoicePayment(
  options: Partial<
    UseMutationOptions<IPayment, unknown, CreateInvoicePaymentRequest>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<IPayment, unknown, CreateInvoicePaymentRequest>({
    mutationFn: ({ type, accountId, invoiceId, payload }) => {
      switch (type) {
        case 'electronic': {
          return apiPost<IPayment>(
            `/api/accounts/${accountId}/payments/invoice/${invoiceId}/pay`,
            payload,
          ).then((res) => res.data);
        }
        case 'achCredit': {
          return apiPost<IPayment>(
            `/api/invoices/${invoiceId}/achCreditPayments`,
            payload,
          ).then((res) => res.data);
        }
        case 'usBankTransfer': {
          return apiPost<IPayment>(
            `/api/invoices/${invoiceId}/achCreditPayments`,
            payload,
          ).then((res) => res.data);
        }
        case 'manual': {
          return apiPost<IPayment>(
            `/api/accounts/${accountId}/payments/invoice/${invoiceId}/pay/manual`,
            payload,
          ).then((res) => res.data);
        }
      }
    },
    onSuccess: (data, variables, context) => {
      const { accountId, invoiceId } = variables;
      queryClient.invalidateQueries({
        queryKey: ['invoices', 'list'],
      });
      queryClient.invalidateQueries({
        queryKey: ['invoices', invoiceId],
      });
      queryClient.invalidateQueries({
        queryKey: [...accountServiceQueryKeys.accounts.invoiceList(accountId)],
      });
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.payments.paymentByAccount(accountId),
        ],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

interface CreateAccountManualPaymentRequest {
  accountId: string;
  payload: IManualPayment;
}

export function useCreateAccountManualPayment(
  options: Partial<
    UseMutationOptions<IPayment, unknown, CreateAccountManualPaymentRequest>
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<IPayment, unknown, CreateAccountManualPaymentRequest>({
    mutationFn: ({ accountId, payload }) =>
      apiPost<IPayment>(
        `/api/accounts/${accountId}/payments/manual`,
        payload,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      const { accountId } = variables;
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.payments.paymentByAccount(accountId),
        ],
      });
      onSuccess && onSuccess(data, variables, context);
    },
    ...restOptions,
  });
}

export const doGetCreditNotesForInvoice = async (invoiceId: string) => {
  const res = await apiGet<ApiListResponse<ICreditNoteSchema>>(
    `/api/invoices/${invoiceId}/creditNotes`,
  );
  return res.data;
};

export const doCreateCreditNoteForInvoice = async (
  invoiceId: string,
  data: ICreditNoteReqSchema,
) => {
  const res = await apiPost<ICreditNoteSchema>(
    `/api/invoices/${invoiceId}/creditNotes`,
    data,
  );
  return res.data;
};

export const doDownloadInvoiceAsCsv = async (invoiceId: string) => {
  const res = await apiGet(`/api/invoices/${invoiceId}/exportCsv`);
  return res.data;
};

export const doPrintInvoiceToPdf = async (
  invoiceId: string,
  invoiceNumber: Maybe<string>,
) => {
  const res = await apiGet<ArrayBuffer>(`/api/invoices/${invoiceId}/print`, {
    responseType: 'arraybuffer',
    headers: new AxiosHeaders({
      Accept: 'application/pdf',
    }),
  });
  return {
    data: res.data,
    fileName: `invoice-${invoiceNumber || invoiceId}.pdf`,
  };
};

export const usePrintInvoiceToHtml = (
  {
    invoiceId,
    lastModifiedTimestamp,
  }: {
    invoiceId: string;
    /** Used for caching response */
    lastModifiedTimestamp?: string;
  },
  options: Partial<UseQueryOptions<string>> = {},
) => {
  return useQuery({
    queryKey: [
      ...invoiceServiceQueryKeys.htmlTemplate(invoiceId),
      lastModifiedTimestamp,
    ],
    queryFn: () =>
      apiGet<string>(`/api/invoices/${invoiceId}/print`, {
        responseType: 'text',
        headers: new AxiosHeaders({
          accept: 'text/html',
        }),
      }).then((res) => res.data),
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData, previousQuery) => previousData,
    ...options,
  });
};

export const usePrintCreditToHtml = (
  {
    creditId,
    lastModifiedTimestamp,
  }: {
    creditId: string;
    /** Used for caching response */
    lastModifiedTimestamp?: string;
  },
  options: Partial<UseQueryOptions<string>> = {},
) => {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.credits.htmlTemplate(creditId),
      lastModifiedTimestamp,
    ],
    queryFn: () =>
      apiGet<string>(`/api/credits/${creditId}/print`, {
        responseType: 'text',
        headers: new AxiosHeaders({
          accept: 'text/html',
        }),
      }).then((res) => res.data),
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData, previousQuery) => previousData,
    ...options,
  });
};

export const doPrintCreditToPdf = async (creditId?: string) => {
  const res = await apiGet<ArrayBuffer>(`/api/credits/${creditId}/print`, {
    responseType: 'arraybuffer',
    headers: new AxiosHeaders({
      accept: 'application/pdf',
    }),
  });
  return { data: res.data, fileName: `credit-${creditId}.pdf` };
};

export function useGetCreditNotesById(
  creditNoteId: string,
  options: Partial<UseQueryOptions<ICreditNoteSchema>> = {},
) {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.creditNotes.creditNoteById(creditNoteId),
    ],
    queryFn: () =>
      apiGet<ICreditNoteSchema>(`/api/creditNotes/${creditNoteId}`).then(
        (res) => res.data,
      ),
    refetchOnWindowFocus: false,
    ...options,
  });
}

export const usePrintCreditNoteToHtml = (
  {
    creditNoteId,
    lastModifiedTimestamp,
  }: {
    creditNoteId: string;
    /** Used for caching response */
    lastModifiedTimestamp?: string;
  },
  options: Partial<UseQueryOptions<string>> = {},
) => {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.creditNotes.htmlTemplate(creditNoteId),
      lastModifiedTimestamp,
    ],
    queryFn: () =>
      apiGet<string>(`/api/creditNotes/${creditNoteId}/print`, {
        responseType: 'text',
        headers: new AxiosHeaders({
          accept: 'text/html',
        }),
      }).then((res) => res.data),
    refetchOnWindowFocus: false,
    retry: false,
    placeholderData: (previousData, previousQuery) => previousData,
    ...options,
  });
};

export const doPrintCreditNoteToPdf = async (creditNoteId?: string) => {
  const res = await apiGet<ArrayBuffer>(
    `/api/creditNotes/${creditNoteId}/print`,
    {
      responseType: 'arraybuffer',
      headers: new AxiosHeaders({
        accept: 'application/pdf',
      }),
    },
  );
  return { data: res.data, fileName: `creditNote-${creditNoteId}.pdf` };
};

// payment gateways apis

export const doGetGatewayAccountByAccount = async (
  accountId: string,
  gatewayId: string,
) => {
  const res = await apiGet<IPaymentGatewayAccountSchema>(
    `/api/accounts/${accountId}/paymentGateways/${gatewayId}/gatewayAccount`,
  );
  return res.data;
};

export function useGetGatewayAccountsByAccount<
  SelectData = IPaymentGatewayAccountSchema[],
>(
  accountId: string,
  options: Partial<
    UseQueryOptions<IPaymentGatewayAccountSchema[], unknown, SelectData>
  > = {},
) {
  return useQuery({
    queryKey: [...accountServiceQueryKeys.payments.paymentByAccount(accountId)],
    queryFn: () =>
      apiGet<IPaymentGatewayAccountSchema[]>(
        `/api/accounts/${accountId}/paymentGatewayAccounts`,
      ).then((res) => res.data),
    ...options,
  });
}

export const doCreatePaymentGatewayByAccount = async (
  accountId: string,
  type: string,
  gatewayId: string,
) => {
  const res = await apiPost<IPaymentGatewayAccountSchema>(
    `/api/accounts/${accountId}/paymentGateways/${gatewayId}/gatewayAccount`,
    {
      gatewayType: type,
    },
  );
  return res.data;
};

export const doCreateOrGetPaymentGatewayByAccount = async (
  accountId: string,
  paymentGatewayType: string,
  paymentGatewayId: string,
) => {
  try {
    const gateway = await doGetGatewayAccountByAccount(
      accountId,
      paymentGatewayId,
    );
    return gateway;
  } catch (err) {
    const gateway = await doCreatePaymentGatewayByAccount(
      accountId,
      paymentGatewayType,
      paymentGatewayId,
    );
    return gateway;
  }
};

export const doCreateGatewayAccountSetupIntent = async (
  gatewayId: string,
  gatewayAccountId: string,
) => {
  // Swagger shows the incorrect response type for this endpoint: https://monetizenow.atlassian.net/browse/BP-6308
  const res = await apiPost<SetupIntentResponse>(
    `/api/paymentGateways/${gatewayId}/gatewayAccounts/${gatewayAccountId}/setupIntent`,
  );
  return res.data;
};

export const doGetGatewayAccountClientSecret = async (
  gatewayId: string,
  gatewayAccountId: string,
) => {
  const res = await apiGet<PaymentGatewayClientSecretResponse>(
    `/api/paymentGateways/${gatewayId}/gatewayAccounts/${gatewayAccountId}/clientSecret`,
  );
  return res.data;
};

// dunning process apis

export function dunningProcessPath(): string {
  return `/api/dunning/dunningProcess`;
}

// End

export function useSetContactOnInvoice(
  options: Partial<
    UseMutationOptions<
      IInvoiceContactUpdateSchema,
      unknown,
      { invoiceId: string; data: IInvoiceContactUpdateSchema }
    >
  > = {},
) {
  return useMutation<
    IInvoiceContactUpdateSchema,
    unknown,
    { invoiceId: string; data: IInvoiceContactUpdateSchema }
  >({
    mutationFn: ({ invoiceId, data }) =>
      apiPut<any>(`/api/invoice/${invoiceId}/contacts`, data).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useUpdatePoNumberOnQuote(options = {}) {
  return useMutation<
    void,
    unknown,
    { quoteId: string; poNumber: Maybe<string> }
  >({
    mutationFn: ({ quoteId, poNumber }) =>
      enqueueForQuote(quoteId, () =>
        apiPut(`/api/quotes/${quoteId}/purchaseOrderNumber`, {
          purchaseOrderNumber: poNumber || null,
        }).then((res) => res.data),
      ),
    ...options,
  });
}

export function useUpdateVatNumberOnQuote(options = {}) {
  return useMutation<
    void,
    unknown,
    { quoteId: string; vatNumber: Maybe<string> }
  >({
    mutationFn: ({ quoteId, vatNumber }) =>
      enqueueForQuote(quoteId, () =>
        apiPut(`/api/quotes/${quoteId}/vatNumber`, {
          vatNumber: vatNumber || null,
        }).then((res) => res.data),
      ),
    ...options,
  });
}

export function useSendEmail(
  options: Partial<
    UseMutationOptions<
      SendBillingEmailResponse,
      unknown,
      { invoiceId: string; overrideCcEmails: Maybe<string[]> }
    >
  > = {},
) {
  return useMutation<
    SendBillingEmailResponse,
    unknown,
    { invoiceId: string; overrideCcEmails: Maybe<string[]> }
  >({
    mutationFn: ({ invoiceId, overrideCcEmails }) =>
      apiPost<SendBillingEmailResponse>(
        `/api/invoices/${invoiceId}/sendBillingEmail`,
        { overrideCcEmails },
      ).then((res) => res.data),
    ...options,
  });
}

export const doPreviewInvoice = async (billGroupId: string) => {
  return await apiGet<ArrayBuffer>(
    `/api/billGroups/${billGroupId}/invoices/preview`,
    {
      responseType: 'arraybuffer',
      headers: new AxiosHeaders({
        accept: 'application/pdf',
      }),
    },
  ).then((res) => res.data);
};

export const usePreviewInvoiceJson = <SelectData = IInvoiceRespSchema>(
  { accountId, billGroupId }: { accountId: string; billGroupId: string },
  options: Partial<
    UseQueryOptions<IInvoiceRespSchema, unknown, SelectData>
  > = {},
) => {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accounts.invoicePreview(
        accountId,
        billGroupId,
      ),
    ],
    queryFn: () =>
      apiGet<IInvoiceRespSchema>(
        `/api/billGroups/${billGroupId}/invoices/preview`,
      ).then((res) => res.data),
    ...options,
  });
};

export function useGeneratePastInvoice(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { accountId: string; billGroupId: string }
    >
  > = {},
) {
  return useMutation<void, unknown, { accountId: string; billGroupId: string }>(
    {
      mutationFn: ({ accountId, billGroupId }) =>
        apiPost(
          `/api/accounts/${accountId}/billGroups/${billGroupId}/manualBatchRun`,
        ).then((res) => res.data),
      ...options,
    },
  );
}

export function useInvokeManualBillRun(
  options: Partial<
    UseMutationOptions<void, unknown, { billGroupIds: string[] }>
  > = {},
) {
  return useMutation<void, unknown, { billGroupIds: string[] }>({
    mutationFn: ({ billGroupIds }) =>
      apiPost(`/api/billGroups/manualBatchRun`, billGroupIds).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useSearchForImportablePaymentMethods(
  options?: UseMutationOptions<
    ImportablePaymentMethodResp,
    AxiosError<GenericApiResponse>,
    any
  >,
) {
  return useMutation({
    mutationFn: ({ gatewayId, gatewayAccountId, accountId }) =>
      apiGet<ImportablePaymentMethodResp>(
        `/api/paymentGateways/${gatewayId}/gatewayAccounts/search?gatewayAccountId=${gatewayAccountId}&accountId=${accountId}`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useImportPaymentMethods(
  options: UseMutationOptions<
    ImportPaymentMethodResp,
    AxiosError<GenericApiResponse>,
    any
  >,
) {
  const { onSuccess, ...rest } = options;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      accountId,
      gatewayId,
      gatewayAccountId,
      externalPaymentMethodIds,
    }) =>
      apiPost<ImportPaymentMethodResp>(
        `/api/accounts/${accountId}/paymentMethods/import`,
        {
          gatewayId,
          gatewayAccountId,
          externalPaymentMethodIds,
        },
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.paymentMethods.paymentMethodList(
            variables.accountId,
          ),
        ],
      });
      options.onSuccess && options.onSuccess(data, variables, context);
    },
    ...rest,
  });
}

export function useUpdateInvoiceCustomFields(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { invoiceId: string; customFields: IInvoiceUpdateSchema['customFields'] }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;
  return useMutation<
    void,
    unknown,
    { invoiceId: string; customFields: IInvoiceUpdateSchema['customFields'] }
  >({
    mutationFn: ({ invoiceId, customFields }) =>
      apiPut<any>(`/api/invoices/${invoiceId}/customFields`, customFields).then(
        (res) => res.data,
      ),

    onSuccess: (response, variables, context) => {
      const { invoiceId } = variables;
      queryClient.invalidateQueries({
        queryKey: [...invoiceServiceQueryKeys.invoiceDetail(invoiceId)],
      });

      onSuccess && onSuccess(response, variables, context);
    },
    ...restOptions,
  });
}

export const useVoidCredit = (
  sourceType = 'credits',
  creditId: string,
  accountId: string,
  options: Partial<
    UseMutationOptions<IVoidCreditSchema, unknown, Partial<IVoidCreditSchema>>
  > = {},
) => {
  const { onSuccess, ...restOptions } = options;
  const queryClient = useQueryClient();

  return useMutation<IVoidCreditSchema, unknown, Partial<IVoidCreditSchema>>({
    mutationFn: (payload) =>
      apiPost<IVoidCreditSchema>(
        `/api/${sourceType}/${creditId}/void`,
        payload,
      ).then((res) => {
        return res.data;
      }),
    onSuccess: (...rest) => {
      queryClient.invalidateQueries({
        queryKey:
          accountServiceQueryKeys.credits.creditListByAccount(accountId),
      });
      if (sourceType === 'credits') {
        queryClient.invalidateQueries({
          queryKey: accountServiceQueryKeys.credits.htmlTemplate(creditId),
        });
        queryClient.invalidateQueries({
          queryKey: accountServiceQueryKeys.credits.creditDetail(creditId),
        });
      }

      if (sourceType === 'creditNotes') {
        queryClient.invalidateQueries({
          queryKey: accountServiceQueryKeys.creditNotes.htmlTemplate(creditId),
        });
        queryClient.invalidateQueries({
          queryKey:
            accountServiceQueryKeys.creditNotes.creditNoteById(creditId),
        });
      }
      onSuccess && onSuccess(...rest);
    },
    ...restOptions,
  });
};

export function useCreatePaymentMethodShareLink(
  options: Partial<
    UseMutationOptions<
      RecordShareAccessResponse,
      unknown,
      { accountId: string; payload: PaymentMethodShareRequest }
    >
  > = {},
) {
  return useMutation<
    RecordShareAccessResponse,
    unknown,
    { accountId: string; payload: PaymentMethodShareRequest }
  >({
    mutationFn: ({ accountId, payload }) => {
      return apiPost<RecordShareAccessResponse>(
        `/api/accounts/${accountId}/paymentMethods/share`,
        payload,
      ).then((res) => res.data);
    },
    ...options,
  });
}

export function useGetAccountDocumentsByAccount<
  SelectData = ApiListResponse<AccountDocumentBasicResponse>,
>(
  {
    accountId,
    config,
    filters,
  }: {
    accountId: string;
    config?: GetListApiConfig;
    filters?: GetListApiFilter;
  },
  options: Partial<
    UseQueryOptions<
      ApiListResponse<AccountDocumentBasicResponse>,
      unknown,
      SelectData
    >
  > = {},
) {
  let params = {};
  if (config) {
    params = composeGetQuery(config, filters);
  }
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
        accountId,
      ),
      params,
    ],
    queryFn: async () =>
      apiGet<ApiListResponse<AccountDocumentBasicResponse>>(
        `/api/accounts/${accountId}/accountDocuments`,
        {
          params,
        },
      ).then((res) => res.data),
    ...options,
  });
}

export function useGetAccountDocumentById(
  accountId: string,
  accountDocumentId: string,
  options: Partial<UseQueryOptions<AccountDocumentDetailResponse>> = {},
) {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accountDocuments.accountDocumentById(
        accountDocumentId,
      ),
    ],
    queryFn: () =>
      apiGet<AccountDocumentDetailResponse>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useCreateAccountDocument(
  options: Partial<
    UseMutationOptions<
      AccountDocumentDetailResponse,
      unknown,
      {
        accountId: string;
        payload: AccountDocumentForm & { file: File | null };
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    AccountDocumentDetailResponse,
    unknown,
    {
      accountId: string;
      payload: AccountDocumentForm & { file: File | null };
    }
  >({
    mutationFn: ({ accountId, payload }) => {
      const formData = new FormData();
      const jsonBlob = new Blob(
        [
          JSON.stringify({
            name: payload.name,
            description: payload.description,
          }),
        ],
        {
          type: 'application/json',
        },
      );
      formData.append('data', jsonBlob, 'blob');
      if (payload.file) {
        formData.append('file', payload.file);
      }
      return apiUpload<AccountDocumentDetailResponse>(
        `/api/accounts/${accountId}/accountDocuments`,
        formData,
      ).then((res) => res.data);
    },
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
            variables.accountId,
          ),
        ],
      });
    },
    ...options,
  });
}

export function useUpdateAccountDocument(
  options: Partial<
    UseMutationOptions<
      AccountDocumentDetailResponse,
      unknown,
      {
        accountId: string;
        accountDocumentId: string;
        payload: AccountDocumentForm;
        file?: File;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    AccountDocumentDetailResponse,
    unknown,
    {
      accountId: string;
      accountDocumentId: string;
      payload: AccountDocumentForm;
      file?: File;
    }
  >({
    mutationFn: ({ accountId, accountDocumentId, payload, file }) => {
      const formData = new FormData();
      const jsonBlob = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      });
      formData.append('data', jsonBlob, 'blob');
      if (file) {
        formData.append('file', file);
      }

      return apiUpload<AccountDocumentDetailResponse>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}`,
        formData,
        undefined,
        undefined,
        true,
      ).then((res) => res.data);
    },
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
            variables.accountId,
          ),
        ],
      });
    },
    ...options,
  });
}

export function useUpdateAccountDocumentContact(
  options: Partial<
    UseMutationOptions<
      AccountDocumentDetailResponse,
      unknown,
      {
        accountId: string;
        accountDocumentId: string;
        payload: {
          esignPrimaryIds: string[];
          esignSecondaryIds: string[];
          esignCcIds: string[];
        };
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    AccountDocumentDetailResponse,
    unknown,
    {
      accountId: string;
      accountDocumentId: string;
      payload: {
        esignPrimaryIds: string[];
        esignSecondaryIds: string[];
        esignCcIds: string[];
      };
    }
  >({
    mutationFn: ({ accountId, accountDocumentId, payload }) => {
      return apiPut<AccountDocumentDetailResponse>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}/contacts`,
        payload,
      ).then((res) => res.data);
    },
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
            variables.accountId,
          ),
        ],
      });
    },
    ...options,
  });
}

export function useGetAccountRefundsByAccount(
  {
    accountId,
    config,
    filters,
  }: {
    accountId: string;
    config?: GetListApiConfig;
    filters?: GetListApiFilter;
  },
  options: Partial<UseQueryOptions<ApiListResponse<IRefundSchema>>> = {},
) {
  let params = {};
  if (config) {
    params = composeGetQuery(config, { ...filters, accountId });
  }

  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.refunds.refundListByAccount(accountId),
      params,
    ],
    queryFn: async () =>
      apiGet<ApiListResponse<IRefundSchema>>(`/api/refunds`, { params }).then(
        (res) => res.data,
      ),
    ...options,
  });
}

export function useSendAccountDocument(
  options: Partial<
    UseMutationOptions<
      AccountDocumentDetailResponse,
      unknown,
      {
        accountId: string;
        accountDocumentId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    AccountDocumentDetailResponse,
    unknown,
    {
      accountId: string;
      accountDocumentId: string;
    }
  >({
    mutationFn: ({ accountId, accountDocumentId }) => {
      return apiPost<AccountDocumentDetailResponse>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}/send`,
      ).then((res) => res.data);
    },
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
            variables.accountId,
          ),
        ],
      });
    },
    ...options,
  });
}
export function useVoidAccountDocument(
  options: Partial<
    UseMutationOptions<
      AccountDocumentDetailResponse,
      unknown,
      {
        accountId: string;
        accountDocumentId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    AccountDocumentDetailResponse,
    unknown,
    {
      accountId: string;
      accountDocumentId: string;
    }
  >({
    mutationFn: ({ accountId, accountDocumentId }) => {
      return apiPost<AccountDocumentDetailResponse>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}/draft`,
      ).then((res) => res.data);
    },
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
            variables.accountId,
          ),
        ],
      });
    },
    ...options,
  });
}

export function useDeleteAccountDocument(
  options: Partial<
    UseMutationOptions<
      AccountDocumentDetailResponse,
      unknown,
      {
        accountId: string;
        accountDocumentId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  return useMutation<
    AccountDocumentDetailResponse,
    unknown,
    {
      accountId: string;
      accountDocumentId: string;
    }
  >({
    mutationFn: ({ accountId, accountDocumentId }) => {
      return apiDelete<AccountDocumentDetailResponse>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}`,
      ).then((res) => res.data);
    },
    onSuccess: (response, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accountDocuments.accountDocumentByAccount(
            variables.accountId,
          ),
        ],
      });
    },
    ...options,
  });
}

export function useGetAccountDocumentSignerStats(
  accountId: string,
  accountDocumentId: string,
  options: Partial<UseQueryOptions<AccountDocumentSignerStatsResponse>> = {},
) {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accountDocuments.accountDocumentSignerStats(
        accountDocumentId,
      ),
    ],
    queryFn: () =>
      apiGet<AccountDocumentSignerStatsResponse>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}/signerStats`,
      ).then((res) => res.data),
    ...options,
  });
}

export function useGetAccountDocumentFile(
  accountId: string,
  accountDocumentId: string,
  storageId: string,
  options: Partial<UseQueryOptions<IStorage>> = {},
) {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accountDocuments.accountDocumentFile(
        accountDocumentId,
        storageId,
      ),
    ],
    queryFn: () =>
      apiGet<IStorage>(
        `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}/attachments/${storageId}?attachContent=true`,
      ).then((res) => res.data),
    ...options,
  });
}

export function doGetAccountDocumentFile(
  accountId: string,
  accountDocumentId: string,
  storageId: string,
) {
  return apiGet<IStorage>(
    `/api/accounts/${accountId}/accountDocuments/${accountDocumentId}/attachments/${storageId}?attachContent=true`,
  ).then((res) => res.data);
}

export function useCreateWireTransferPaymentMethod(
  options: Partial<
    UseMutationOptions<
      IPaymentMethodResp,
      unknown,
      {
        accountId: string;
        paymentGatewayId: string;
        payload: WireTransferPaymentMethodCreateReq;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...rest } = options;
  return useMutation<
    IPaymentMethodResp,
    unknown,
    {
      accountId: string;
      paymentGatewayId: string;
      payload: WireTransferPaymentMethodCreateReq;
    }
  >({
    mutationFn: ({ accountId, paymentGatewayId, payload }) =>
      apiPost<IPaymentMethodResp>(
        `/api/accounts/${accountId}/paymentGateways/${paymentGatewayId}/paymentMethods/ach`,
        payload,
      ).then((res) => res.data),
    onSuccess: (data, variables, context) => {
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.paymentMethods.paymentMethodList(
            variables.accountId,
          ),
        ],
      });

      //Cache the newly created payment method by its ID
      if (data?.id) {
        queryClient.setQueryData(
          accountServiceQueryKeys.paymentMethods.paymentMethod(data.id),
          data,
        );
      }

      onSuccess && onSuccess(data, variables, context);
    },
    ...rest,
  });
}

export const updateInvoicePaymentInstructions = async (
  invoiceId: string,
  accountId: string,
  payload: {
    paymentInstruction: IPaymentInstructionSchema | null;
  },
) => {
  const res = await apiPut<IPaymentInstructionSchema>(
    `/api/accounts/${accountId}/invoice/${invoiceId}/paymentInstructions`,
    payload,
  );
  return res.data;
};

export function useUnlinkPaymentGatewayAccount(
  options: Partial<
    UseMutationOptions<
      GenericApiResponse,
      unknown,
      {
        payload: PaymentGatewayAccountUnlinkReq;
        accountId: string;
        paymentGatewayId: string;
        gatewayCustomerId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options;

  return useMutation({
    mutationFn: ({ accountId, paymentGatewayId, gatewayCustomerId, payload }) =>
      apiPut<GenericApiResponse>(
        `/api/accounts/${accountId}/paymentGateways/${paymentGatewayId}/gatewayAccount/${gatewayCustomerId}/paymentMethods`,

        payload,
      ).then((res) => res.data),

    onSuccess: (data, variables, context) => {
      if (variables.accountId) {
        queryClient.invalidateQueries({
          queryKey: accountServiceQueryKeys.paymentMethods.paymentMethodList(
            variables.accountId,
          ),
        });
        queryClient.invalidateQueries({
          queryKey: accountServiceQueryKeys.payments.paymentByAccount(
            variables.accountId,
          ),
        });
      }

      onSuccess && onSuccess(data, variables, context);
    },

    ...restOptions,
  });
}

export function useCreateGatewayAccount(
  options: Partial<
    UseMutationOptions<
      IPaymentGatewayAccountSchema,
      unknown,
      {
        accountId: string;
        paymentGatewayId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();

  const { onSuccess, ...restOptions } = options;

  return useMutation<
    IPaymentGatewayAccountSchema,
    unknown,
    {
      accountId: string;
      paymentGatewayId: string;
    }
  >({
    mutationFn: ({ accountId, paymentGatewayId }) =>
      apiPost<IPaymentGatewayAccountSchema>(
        `/api/accounts/${accountId}/paymentGateways/${paymentGatewayId}/gatewayAccount`,
      ).then((res) => res.data),

    onSuccess: (data, variables, context) => {
      if (variables.accountId) {
        queryClient.invalidateQueries({
          queryKey: accountServiceQueryKeys.payments.paymentByAccount(
            variables.accountId,
          ),
        });
      }

      onSuccess && onSuccess(data, variables, context);
    },

    ...restOptions,
  });
}

export function useLinkGatewayCustomerToAccount(
  options: Partial<
    UseMutationOptions<
      IPaymentGatewayAccountSchema,
      unknown,
      {
        accountId: string;
        paymentGatewayId: string;
        gatewayCustomerId: string;
      }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  const { onSuccess, ...restOptions } = options;

  return useMutation<
    IPaymentGatewayAccountSchema,
    unknown,
    {
      accountId: string;
      paymentGatewayId: string;
      gatewayCustomerId: string;
    }
  >({
    mutationFn: ({ accountId, paymentGatewayId, gatewayCustomerId }) =>
      apiPut<IPaymentGatewayAccountSchema>(
        `/api/accounts/${accountId}/paymentGateways/${paymentGatewayId}/gatewayAccounts/${gatewayCustomerId}`,
      ).then((res) => res.data),

    onSuccess: (data, variables, context) => {
      // to trigger a re render of payment method list component
      queryClient.invalidateQueries({
        queryKey: accountServiceQueryKeys.payments.paymentByAccount(
          variables.accountId,
        ),
      });

      onSuccess && onSuccess(data, variables, context);
    },

    ...restOptions,
  });
}

export function useMergeAccounts(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      {
        sourceAccountId: string;
        targetAccountId: string;
        retainSourcePaymentGatewayAccount: boolean;
      }
    >
  > = {},
) {
  return useMutation<
    void,
    unknown,
    {
      sourceAccountId: string;
      targetAccountId: string;
      retainSourcePaymentGatewayAccount: boolean;
    }
  >({
    mutationFn: (payload) =>
      apiPost(`/api/accounts/merge`, payload).then((res) => res.data),
    ...options,
  });
}

type AccountHierarchyResponse<T extends AccountHierarchyResponseTypeEnum> =
  T extends AccountHierarchyResponseTypeEnum.FLAT
    ? AccountHierarchyFlat
    : AccountHierarchyNested;

/**
 * The response type is dynamic based on the responseType parameter.
 * If responseType is 'FLAT', the response will be of type AccountHierarchyFlat.
 * If responseType is 'NESTED', the response will be of type AccountHierarchyNested.
 */
export function useGetAccountHierarchyById<
  Type extends AccountHierarchyResponseTypeEnum,
>(
  accountId: string,
  responseType: Type,
  options: Partial<
    UseQueryOptions<
      AccountHierarchyResponse<Type>,
      unknown,
      AccountHierarchyResponse<Type>
    >
  > = {},
) {
  return useQuery({
    queryKey: [
      ...accountServiceQueryKeys.accounts.accountHierarchyById(accountId),
      responseType,
    ],
    queryFn: () =>
      apiGet<AccountHierarchyResponse<Type>>(
        `/api/accounts/${accountId}/hierarchy`,
        { params: { responseType } },
      ).then((res) => res.data),
    ...options,
  });
}

export function useUpdateAccountHierarchy(
  options: Partial<
    UseMutationOptions<
      void,
      unknown,
      { accountId: string; parentAccountId: string | null }
    >
  > = {},
) {
  const queryClient = useQueryClient();
  return useMutation<
    void,
    unknown,
    { accountId: string; parentAccountId: string | null }
  >({
    mutationFn: ({ accountId, parentAccountId }) =>
      apiPut(`/api/accounts/${accountId}/parent`, { parentAccountId }).then(
        (res) => res.data,
      ),
    onSuccess: (_data, variables, _context) => {
      // Invalidate the account hierarchy query to reflect the changes
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accounts.accountHierarchyById(
            variables.accountId,
          ),
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [
          ...accountServiceQueryKeys.accounts.accountDetail(
            variables.accountId,
          ),
        ],
      });
    },
    ...options,
  });
}
